import ModalSurface from '../shared/ModalSurface';
import '../financials/financialsResponsive.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { describeCard } from '../../lib/stripeCards';
import { resolveInvoiceAmount } from '../../lib/stripeInvoiceAmounts';
import ActionPillMenu from '../ui/ActionPillMenu';

const EDGE_FUNCTION = 'stripe-setup-intent';
const SETTLED_STATUSES = new Set(['paid', 'closed', 'void', 'voided', 'cancelled', 'canceled', 'refunded']);

function invoiceStatus(invoice) {
    return [invoice?.lifecycle_status, invoice?.status]
        .map((value) => String(value || '').trim().toLowerCase());
}

function isSettled(invoice) {
    return invoiceStatus(invoice).some((status) => SETTLED_STATUSES.has(status));
}

export default function ChargeInvoiceAction({
    invoice,
    client = supabaseClient,
    onInvoiceStatusChange,
    pollIntervalMs = 2000,
    maxPollAttempts = 15,
}) {
    const [card, setCard] = useState(null);
    const [loadingCard, setLoadingCard] = useState(true);
    const [chargeError, setChargeError] = useState('');
    const [message, setMessage] = useState('');
    const [pendingIntent, setPendingIntent] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const cardRequestRef = useRef(null);
    const submittingRef = useRef(false);
    const onStatusChangeRef = useRef(onInvoiceStatusChange);
    onStatusChangeRef.current = onInvoiceStatusChange;
    const accountId = invoice?.account_id || '';
    const chargeable = Boolean(invoice?.id && accountId && !isSettled(invoice));
    const chargeAmount = resolveInvoiceAmount(invoice);
    const formattedChargeAmount = chargeAmount === null
        ? null
        : new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(chargeAmount);

    const loadCard = useCallback(() => {
        if (!accountId || cardRequestRef.current) return cardRequestRef.current;
        setLoadingCard(true);
        setChargeError('');
        cardRequestRef.current = (async () => {
            try {
                const { data, error } = await client.functions.invoke(EDGE_FUNCTION, {
                    body: {
                        action: 'list_payment_methods',
                        ownerType: 'account',
                        accountId,
                        portalAccountId: null,
                        label: 'company',
                    },
                });
                if (error) throw new Error(error.message || 'Saved cards could not be loaded.');
                if (data?.error) throw new Error(data.error);
                setCard((data?.paymentMethods || []).find((item) => item.is_default) || data?.paymentMethods?.[0] || null);
            } catch (error) {
                cardRequestRef.current = null;
                setChargeError(error.message || 'Saved cards could not be loaded.');
            } finally {
                setLoadingCard(false);
            }
        })();
        return cardRequestRef.current;
    }, [accountId, client]);

    const handleMenuOpenChange = useCallback((nextOpen) => {
        setMenuOpen(nextOpen);
        if (nextOpen) loadCard();
    }, [loadCard]);

    useEffect(() => {
        if (!pendingIntent || !invoice?.id) return undefined;
        let active = true;
        let timer;
        let attempts = 0;

        async function refreshInvoice() {
            attempts += 1;
            const { data, error } = await client
                .from('invoices')
                .select('id, org_id, account_id, status, lifecycle_status')
                .eq('id', invoice.id)
                .maybeSingle();
            if (!active) return;
            if (error) {
                setChargeError(`Charge submitted, but payment confirmation could not be refreshed: ${error.message}`);
                setPendingIntent(null);
                return;
            }
            if (data) {
                onStatusChangeRef.current?.(data);
                if (isSettled(data)) {
                    setMessage('Payment confirmed. The invoice status has been updated.');
                    setPendingIntent(null);
                    return;
                }
            }
            if (attempts >= maxPollAttempts) {
                setMessage('Charge submitted. The invoice is still awaiting webhook confirmation; its status will update when Stripe confirms payment.');
                setPendingIntent(null);
                return;
            }
            timer = setTimeout(refreshInvoice, pollIntervalMs);
        }

        timer = setTimeout(refreshInvoice, pollIntervalMs);
        return () => {
            active = false;
            clearTimeout(timer);
        };
    }, [client, invoice?.id, maxPollAttempts, pendingIntent, pollIntervalMs]);

    const charge = useCallback(async () => {
        if (!card || !invoice?.id || !accountId || chargeAmount === null || submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        setChargeError('');
        setMessage('');
        try {
            const { data, error } = await client.functions.invoke(EDGE_FUNCTION, {
                body: {
                    action: 'charge_invoice',
                    invoiceId: invoice.id,
                    ownerType: 'account',
                    accountId,
                    portalAccountId: null,
                    label: 'company',
                    paymentMethodRowId: card.id,
                },
            });
            if (error) throw new Error(error.message || 'The charge could not be submitted.');
            if (data?.error) throw new Error(data.error);
            if (!data?.ok || !data?.paymentIntentId) {
                throw new Error('The payment service did not confirm that the charge was submitted.');
            }
            setMessage('Charge submitted. Waiting for Stripe webhook confirmation…');
            setPendingIntent(data.paymentIntentId);
            setConfirmOpen(false);
        } catch (error) {
            setChargeError(error.message || 'The card could not be charged.');
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    }, [accountId, card, chargeAmount, client, invoice?.id]);

    if (!chargeable) return null;

    const disabled = loadingCard || !card || chargeAmount === null || Boolean(pendingIntent) || submitting;
    const cardName = card ? describeCard(card) : 'saved company card';
    const options = [{
        key: 'charge-saved-card',
        label: loadingCard
            ? 'Loading saved card…'
            : card
                ? `Charge ${cardName}`
                : chargeAmount === null
                    ? 'Invoice amount unavailable'
                : chargeError
                    ? 'Saved card unavailable'
                    : 'No saved company card',
        disabled,
        onSelect: () => setConfirmOpen(true),
    }];

    return (
        <div className="ia-financials space-y-1">
            <ActionPillMenu
                menuLabel={`Charge invoice ${invoice.invoice_number || invoice.id}`}
                triggerLabel="Charge"
                triggerTitle={loadingCard ? 'Loading saved card' : card ? `Charge ${cardName}` : 'No saved company card'}
                triggerClassName="rounded-full border border-[var(--ui-border)] bg-transparent px-3 py-1 text-xs font-semibold text-[var(--ink)] hover:border-[#03B7C9] hover:text-[#03B7C9] ia-hover-pulse"
                menuDisabled={!chargeable || Boolean(pendingIntent)}
                open={menuOpen}
                onOpenChange={handleMenuOpenChange}
                options={options}
            />
            {loadingCard && <p className="text-xs text-[var(--ink-muted)]">Loading saved card…</p>}
            {!loadingCard && !card && !chargeError && <p className="text-xs text-[var(--ink-muted)]">No saved company card is available.</p>}
            {chargeAmount === null && <p className="text-xs text-[var(--ink-muted)]">This invoice has no chargeable amount recorded.</p>}
            {chargeError && <p className="text-xs text-red-600" role="alert">{chargeError}</p>}
            {message && <p className="text-xs text-[var(--ink-muted)]" role="status">{message}</p>}
            {confirmOpen && (
                <ModalSurface label="invoice charge" labelledBy="charge-confirmation-title"
                    onClose={() => setConfirmOpen(false)} closeDisabled={submitting}
                    overlayClassName="ia-financials fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onOverlayClick={(event) => {
                        if (event.target === event.currentTarget && !submitting) setConfirmOpen(false);
                    }}
                    panelClassName="w-full max-w-md space-y-4 rounded-xl border border-[var(--surface-border)] bg-[var(--surface-raised)] p-5 text-[var(--ink)] shadow-xl"
                >
                    <div className="ia-modal-body space-y-4">
                        <div>
                            <h2 id="charge-confirmation-title" className="text-lg font-semibold">Confirm invoice charge</h2>
                            <p id="charge-confirmation-details" className="mt-2 text-sm text-[var(--ink-muted)]">
                                Charge <strong className="text-[var(--ink)]">{formattedChargeAmount}</strong> to{' '}
                                <strong className="text-[var(--ink)]">{cardName}</strong>
                                {invoice.invoice_number ? ` for invoice ${invoice.invoice_number}` : ''}?
                            </p>
                        </div>
                        {chargeError && <p className="text-sm text-red-600" role="alert">{chargeError}</p>}
                        <div className="ia-modal-actions flex justify-end gap-2">
                            <button
                                type="button"
                                disabled={submitting}
                                onClick={() => setConfirmOpen(false)}
                                className="rounded-lg border border-[var(--surface-border)] px-3 py-2 text-sm text-[var(--ink)] disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={disabled}
                                onClick={charge}
                                className="ia-hover-pulse rounded-lg bg-[var(--btn-fill)] px-3 py-2 text-sm font-medium text-[var(--btn-ink)] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {submitting ? 'Submitting…' : 'Confirm charge'}
                            </button>
                        </div>
                    </div>
                </ModalSurface>
            )}
        </div>
    );
}
